import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Loader } from 'lucide-react';
import { API_BASE } from '../utils/apiBase';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(p => p.category === activeCategory));
    }
  }, [activeCategory, products]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_BASE}/products`);
      // Show products that are explicitly active, or legacy products without status.
      const activeProducts = response.data.filter(
        (p) => !p.status || p.status.toLowerCase() === 'active'
      );
      setProducts(activeProducts);
      setFilteredProducts(activeProducts);
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const hairProducts = products.filter(p => p.category === 'hair');
  const skinProducts = products.filter(p => p.category === 'skin');

  return (
    <div className="products-page">
      {/* Hero Section */}
      <section className="bg-cream py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold text-center text-dark-text mb-6" data-testid="products-heading">
            Amrita Sparsha Wellness Products
          </h1>
          <p className="text-xl text-center text-gray-700 max-w-3xl mx-auto" data-testid="products-intro">
            Amrita Sparsha products are designed to <strong>support skin, hair, and personal wellness</strong> as part of a balanced care routine. They are not intended to diagnose or cure medical conditions.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => setActiveCategory('all')}
              data-testid="category-all"
              className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                activeCategory === 'all'
                  ? 'bg-golden text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-light-gold'
              }`}
            >
              All Products ({products.length})
            </button>
            <button
              onClick={() => setActiveCategory('hair')}
              data-testid="category-hair"
              className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                activeCategory === 'hair'
                  ? 'bg-golden text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-light-gold'
              }`}
            >
              Hair Care ({hairProducts.length})
            </button>
            <button
              onClick={() => setActiveCategory('skin')}
              data-testid="category-skin"
              className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                activeCategory === 'skin'
                  ? 'bg-golden text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-light-gold'
              }`}
            >
              Skin Care ({skinProducts.length})
            </button>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center py-20" data-testid="products-loading">
              <Loader className="animate-spin text-golden" size={48} />
            </div>
          ) : error ? (
            <div className="text-center py-20" data-testid="products-error">
              <p className="text-red-600 text-lg">{error}</p>
              <button 
                onClick={fetchProducts}
                className="btn-primary px-6 py-3 rounded-lg mt-4"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="products-grid">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-cream">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-dark-text mb-6" data-testid="products-cta-heading">
            Need Personalized Guidance?
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            For chronic concerns or personalized treatment plans, consult with Dr. Sanjay Panicker.
          </p>
          <a 
            href="/contact" 
            className="btn-primary px-8 py-4 rounded-lg font-semibold text-lg inline-block"
            data-testid="products-cta-consult-button"
          >
            Book Consultation
          </a>
        </div>
      </section>
    </div>
  );
};

export default Products;
